import React from 'react';
import { BrokerPosition } from '../../store/brokerStore';
import { DollarSign, RefreshCw } from 'lucide-react';

interface PositionsSummaryProps {
  positions: BrokerPosition[];
  totalPnL: number;
  workingOrdersCount: number;
  connectedBrokersCount: number;
  isExpanded: boolean;
  onClick: () => void;
  onRefresh: () => void;
}

export const PositionsSummary: React.FC<PositionsSummaryProps> = ({
  positions,
  totalPnL,
  workingOrdersCount,
  connectedBrokersCount,
  isExpanded,
  onClick,
  onRefresh,
}) => {
  // Calculate net position across all accounts
  const netPosition = positions.reduce((sum, pos) => sum + pos.netPos, 0);
  const totalQty = positions.reduce((sum, pos) => sum + Math.abs(pos.netPos), 0);
  const isLong = netPosition > 0;
  const isShort = netPosition < 0;
  const isFlat = netPosition === 0;
  const isMixed = positions.length > 1 && positions.some(p => p.netPos > 0) && positions.some(p => p.netPos < 0);

  // Determine position label
  let positionLabel = 'FLAT';
  let positionClass = 'flat';
  if (isMixed) {
    positionLabel = 'MIX';
    positionClass = 'mixed';
  } else if (isLong) {
    positionLabel = 'L';
    positionClass = 'long';
  } else if (isShort) {
    positionLabel = 'S';
    positionClass = 'short';
  }

  const pnlClass = totalPnL >= 0 ? 'positive' : 'negative';

  const handleRefreshClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the summary onClick
    onRefresh();
  };

  return (
    <div className="abp-summary-container">
      <button
        className={`abp-summary ${isExpanded ? 'expanded' : ''} ${positionClass}`}
        onClick={onClick}
        title="Click to view positions & orders"
      >
        {/* Dollar icon */}
        <DollarSign size={12} className="abp-summary-icon" />

        {/* Position indicator */}
        {!isFlat && (
          <span className={`abp-summary-position ${positionClass}`}>
            {totalQty}{positionLabel}
          </span>
        )}

        {/* P&L */}
        <span className={`abp-summary-pnl ${pnlClass}`}>
          {totalPnL >= 0 ? '+' : ''}{totalPnL < 0 ? '-' : ''}${Math.abs(totalPnL).toFixed(2)}
        </span>

        {/* Connected brokers count */}
        {connectedBrokersCount > 0 && (
          <span className="abp-summary-brokers" title={`${connectedBrokersCount} connected broker${connectedBrokersCount > 1 ? 's' : ''}`}>
            {connectedBrokersCount}B
          </span>
        )}

        {/* Working orders indicator */}
        {workingOrdersCount > 0 && (
          <span className="abp-summary-orders" title={`${workingOrdersCount} working orders`}>
            {workingOrdersCount}
          </span>
        )}
      </button>

      {/* Refresh button - always visible */}
      <button
        className="abp-summary-refresh"
        onClick={handleRefreshClick}
        title="Refresh positions & orders"
      >
        <RefreshCw size={12} />
      </button>
    </div>
  );
};
